import { useCallback, useEffect, useMemo, useState } from 'react';
import { OPTIMISTIC_ID_PREFIX } from 'src/graphql/entities/custom-view/constants';
import { Macro } from 'src/graphql/entities/macro/macro';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import {
  useCreateUserSettingValue,
  UserSettingId,
  UserSettingMappedValue,
  useUpdateUserSettingValue,
} from 'src/graphql/entities/user-setting-value';

export function useFavoriteMacros({ macros }: { macros: Macro[] }) {
  const { user } = useCurrentUserProfile();
  const { createUserSettingValue, isLoading: isCreating } = useCreateUserSettingValue();
  const { updateUserSettingValue, isLoading: isUpdating } = useUpdateUserSettingValue();
  const [localFavoriteMacroIds, setLocalFavoriteMacroIds] = useState<string[] | null>(null);

  const allowedMacroIds = useMemo(() => new Set(macros.map((macro) => macro.id)), [macros]);

  const favoriteMacroSetting = useMemo(
    () => user?.userSettingValueMap.get(UserSettingId.FavoriteMacros),
    [user?.userSettingValueMap],
  );

  const persistedFavoriteMacroIds = useMemo(() => {
    const storedIds = normalizeFavoriteMacroIds(favoriteMacroSetting?.value);
    return storedIds.filter((macroId) => allowedMacroIds.has(macroId));
  }, [allowedMacroIds, favoriteMacroSetting?.value]);

  const persistedOutOfScopeFavoriteMacroIds = useMemo(() => {
    const storedIds = normalizeFavoriteMacroIds(favoriteMacroSetting?.value);
    return storedIds.filter((macroId) => !allowedMacroIds.has(macroId));
  }, [allowedMacroIds, favoriteMacroSetting?.value]);

  useEffect(() => {
    if (!favoriteMacroSetting) {
      return;
    }
    const settingId = favoriteMacroSetting.userSettingValue.id;
    const isOptimisticSetting = settingId.startsWith(OPTIMISTIC_ID_PREFIX);
    if (!isOptimisticSetting) {
      setLocalFavoriteMacroIds(null);
    }
  }, [favoriteMacroSetting]);

  const favoriteMacroIds = useMemo(() => {
    return (localFavoriteMacroIds ?? persistedFavoriteMacroIds).filter((macroId) =>
      allowedMacroIds.has(macroId),
    );
  }, [allowedMacroIds, localFavoriteMacroIds, persistedFavoriteMacroIds]);

  const persistFavoriteMacroIds = useCallback(
    (macroIds: string[]) => {
      if (!user?.userId) {
        return;
      }

      const sanitizedIds = [...new Set(macroIds)].filter((macroId) =>
        allowedMacroIds.has(macroId),
      );
      const value = [
        ...new Set([...persistedOutOfScopeFavoriteMacroIds, ...sanitizedIds]),
      ].join(',');

      const currentPersistedValue = normalizeFavoriteMacroIds(
        favoriteMacroSetting?.value,
      ).join(',');

      if (currentPersistedValue === value) {
        return;
      }

      setLocalFavoriteMacroIds(sanitizedIds);

      if (favoriteMacroSetting) {
        updateUserSettingValue(favoriteMacroSetting.userSettingValue, { value });
        return;
      }

      createUserSettingValue({
        userSettingId: UserSettingId.FavoriteMacros,
        value,
        valueType: 'string[]',
      });
    },
    [
      allowedMacroIds,
      createUserSettingValue,
      favoriteMacroSetting,
      persistedOutOfScopeFavoriteMacroIds,
      updateUserSettingValue,
      user?.userId,
    ],
  );

  const toggleFavoriteMacro = useCallback(
    (macroId: string) => {
      const nextIds = favoriteMacroIds.includes(macroId)
        ? favoriteMacroIds.filter((id) => id !== macroId)
        : [...favoriteMacroIds, macroId];

      persistFavoriteMacroIds(nextIds);
    },
    [favoriteMacroIds, persistFavoriteMacroIds],
  );

  const sanitizeFavoriteMacros = useCallback(() => {
    const sanitizedIds = normalizeFavoriteMacroIds(favoriteMacroSetting?.value);
    const storedValue =
      typeof favoriteMacroSetting?.value === 'string' ? favoriteMacroSetting.value : null;
    const normalizedValue = sanitizedIds.join(',');

    if (storedValue === normalizedValue) {
      return;
    }

    persistFavoriteMacroIds(sanitizedIds);
  }, [favoriteMacroSetting?.value, persistFavoriteMacroIds]);

  return {
    favoriteMacroIds,
    toggleFavoriteMacro,
    sanitizeFavoriteMacros,
    isLoading: isCreating || isUpdating,
  };
}

function normalizeFavoriteMacroIds(value?: UserSettingMappedValue): string[] {
  if (Array.isArray(value)) {
    return value
      .map(String)
      .map((id) => id.trim())
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  return [];
}
